'use client'

import { Box, useMediaQuery } from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import { type PropsWithChildren, createContext, useCallback, useContext, useMemo, useState } from 'react'

import { useSettings } from '@core/context/settingsContext'

import useIsReadOnly from '~/auth/hooks/useIsReadOnly'
import { useGateValue } from '~/configs/statsig'
import { useCurrentOrgHasInsights } from '~/data/api/hooks/organizations'

import PendingDraftApplier from './PendingDraftApplier'
import type { BillingComposerEntity, BillingComposerMode } from './types'

/** Width of the docked panel on md+ screens; the page content shrinks by the same amount. */
export const BILLING_COMPOSER_PANEL_WIDTH = 440

export type OpenComposerOptions = {
  /** Message to send as soon as the panel opens (starter prompt, "Fix with Agent", ...). */
  prompt?: string
}

export type BillingComposerContextValue = {
  /** False when either gate is off, the org lacks Insights, or the user is read-only: every entry point hides. */
  enabled: boolean
  isOpen: boolean
  /** Undefined on general surfaces (billing hub, tax hub) where the panel does Q&A and completeness only. */
  entity?: BillingComposerEntity
  mode: BillingComposerMode
  entityId?: string
  entityName?: string
  /** Page title sent as assistant page context. */
  pageTitle: string
  openComposer: (options?: OpenComposerOptions) => void
  closeComposer: () => void
  /** Prompt queued by {@link openComposer}; the panel sends it once mounted and then clears it. */
  pendingPrompt: string | null
  clearPendingPrompt: () => void
}

const BillingComposerContext = createContext<BillingComposerContextValue | null>(null)

type BillingComposerProviderProps = PropsWithChildren<{
  entity?: BillingComposerEntity
  mode: BillingComposerMode
  entityId?: string
  entityName?: string
  pageTitle: string
}>

/**
 * Per-page state for the billing composer: whether the panel is open, which entity/mode the page is
 * about, and a one-shot prompt to send on open. Wrap the page content (inside the wizard's
 * `FormProvider` when there is one) so the panel can read the live form, and mount
 * `BillingComposerPanel` anywhere inside. On md+ the children shrink to make room for the docked panel.
 */
export default function BillingComposerProvider({
  children,
  entity,
  mode,
  entityId,
  entityName,
  pageTitle
}: BillingComposerProviderProps) {
  const composerGate = useGateValue('billing-composer')
  const assistantGate = useGateValue('ai-assistant')
  const hasInsights = useCurrentOrgHasInsights()
  const readOnly = useIsReadOnly()
  const enabled = composerGate && assistantGate && Boolean(hasInsights) && !readOnly

  const theme = useTheme()
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'))
  const { settings, saveSettings } = useSettings()

  const [isOpen, setIsOpen] = useState(false)
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null)

  const openComposer = useCallback(
    (options?: OpenComposerOptions) => {
      if (!enabled) return
      setIsOpen(true)
      if (options?.prompt) setPendingPrompt(options.prompt)
      // Buy the panel's width back from the left nav, as the other content-shifting drawers do.
      if (!settings.navCollapsed) saveSettings({ ...settings, navCollapsed: true })
    },
    [enabled, saveSettings, settings]
  )

  const closeComposer = useCallback(() => setIsOpen(false), [])
  const clearPendingPrompt = useCallback(() => setPendingPrompt(null), [])

  const value = useMemo<BillingComposerContextValue>(
    () => ({
      enabled,
      isOpen: enabled && isOpen,
      entity,
      mode,
      entityId,
      entityName,
      pageTitle,
      openComposer,
      closeComposer,
      pendingPrompt,
      clearPendingPrompt
    }),
    [clearPendingPrompt, closeComposer, enabled, entity, entityId, entityName, isOpen, mode, openComposer, pageTitle, pendingPrompt]
  )

  return (
    <BillingComposerContext.Provider value={value}>
      <PendingDraftApplier entity={entity} mode={mode} entityId={entityId} />
      <ShiftingContent open={enabled && isOpen && isMdUp}>{children}</ShiftingContent>
    </BillingComposerContext.Provider>
  )
}

/** The composer context, or null outside a provider (entry points render nothing in that case). */
export function useOptionalBillingComposer(): BillingComposerContextValue | null {
  return useContext(BillingComposerContext)
}

export function useBillingComposerContext(): BillingComposerContextValue {
  const value = useContext(BillingComposerContext)
  if (!value) {
    throw new Error('useBillingComposerContext must be used within a BillingComposerProvider')
  }
  return value
}

const ShiftingContent = styled(Box, { shouldForwardProp: (prop) => prop !== 'open' })<{ open: boolean }>(
  ({ theme, open }) => ({
    width: open ? `calc(100% - ${BILLING_COMPOSER_PANEL_WIDTH}px)` : '100%',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.enteringScreen
    })
  })
)
